import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import config from 'lib/config'
import {
  fetchBillingStatus,
  openShopifySelectPlan,
  openShopifyViewBilling,
  BillingStatusResponse,
} from 'utils/billingApi'

type Props = {
  enabled: boolean
}

function dismissKey(status: BillingStatusResponse): string {
  return `paywall-dismiss:${status.status || ''}:${status.trialEndsAt || ''}`
}

function applyStatusPayload(
  body: BillingStatusResponse | null,
  setStatus: (s: BillingStatusResponse | null) => void,
  setVisible: (v: boolean) => void
) {
  if (!body || !body.ok) {
    setStatus(null)
    setVisible(false)
    return
  }
  setStatus(body)
  const shouldShow = !!(body.required || body.warn)
  if (!shouldShow) {
    setVisible(false)
    return
  }
  try {
    if (sessionStorage.getItem(dismissKey(body)) === '1' && !body.required) {
      setVisible(false)
      return
    }
  } catch (_) {
    /* ignore */
  }
  setVisible(true)
}

const PaywallBanner: FC<Props> = ({ enabled }) => {
  const history = useHistory()
  const [status, setStatus] = useState<BillingStatusResponse | null>(null)
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)
  const requestGen = useRef(0)

  const refresh = useCallback(() => {
    if (!enabled || !config.billingApiBase) {
      requestGen.current += 1
      setStatus(null)
      setVisible(false)
      return Promise.resolve()
    }
    const gen = ++requestGen.current
    return fetchBillingStatus()
      .then((body) => {
        if (gen !== requestGen.current) return
        applyStatusPayload(body, setStatus, setVisible)
      })
      .catch(() => {
        if (gen !== requestGen.current) return
        setStatus(null)
        setVisible(false)
      })
  }, [enabled])

  useEffect(() => {
    refresh()
    return () => {
      requestGen.current += 1
    }
  }, [refresh])

  useEffect(() => {
    if (!enabled) return
    const onFocus = () => {
      refresh()
    }
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [enabled, refresh])

  const onSelectPlan = useCallback(async () => {
    setBusy(true)
    try {
      if (status?.billingProvider === 'stripe' || status?.billingProvider === 'none') {
        history.push('/billing')
        return
      }
      const result = await openShopifySelectPlan(status)
      if (!result.ok) history.push('/billing')
    } finally {
      setBusy(false)
    }
  }, [history, status])

  const onViewBilling = useCallback(async () => {
    setBusy(true)
    try {
      if (status?.billingProvider !== 'shopify') {
        history.push('/billing')
        return
      }
      const result = await openShopifyViewBilling(status)
      if (!result.ok) history.push('/billing')
    } finally {
      setBusy(false)
    }
  }, [history, status])

  const onDismiss = useCallback(() => {
    if (status?.required) {
      history.push('/billing')
      return
    }
    if (status) {
      try {
        sessionStorage.setItem(dismissKey(status), '1')
      } catch (_) {
        /* ignore */
      }
    }
    setVisible(false)
  }, [history, status])

  if (!enabled || !visible || !status) return null

  const days = status.remainingDays
  let message = 'Your trial is ending soon. Update billing to keep full portal access.'
  if (status.required) {
    message = 'Your trial has ended. Choose a plan to continue using the portal.'
  } else if (days === 1) {
    message = `Your ${status.plan || 'trial'} ends tomorrow.`
  } else if (days != null && days > 1) {
    message = `Your ${status.plan || 'trial'} ends in ${days} days.`
  }

  const isShopify = status.billingProvider === 'shopify'

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 100000,
        maxWidth: 720,
        margin: '0 auto',
        padding: '12px 16px',
        borderRadius: 10,
        background: status.required ? 'rgba(45, 55, 72, 0.96)' : 'rgba(55, 65, 81, 0.94)',
        color: '#f9fafb',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        fontSize: 14,
        lineHeight: 1.4,
      }}
    >
      <div style={{ flex: '1 1 220px' }}>{message}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={busy}
          onClick={onSelectPlan}
          style={{
            border: 'none',
            borderRadius: 8,
            padding: '8px 14px',
            background: '#2e5bff',
            color: '#fff',
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          {busy ? 'Opening…' : isShopify ? 'Select plan' : 'Update billing'}
        </button>
        {isShopify ? (
          <button
            type="button"
            disabled={busy}
            onClick={onViewBilling}
            style={{
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 8,
              padding: '8px 14px',
              background: 'transparent',
              color: '#fff',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            View billing
          </button>
        ) : null}
        {!status.required ? (
          <button
            type="button"
            onClick={onDismiss}
            style={{
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 8,
              padding: '8px 14px',
              background: 'transparent',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default PaywallBanner
